import { useEffect, useState } from 'react';
import moment from 'moment-timezone';

/**
 * Generate timezone-aware date ranges
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York', 'UTC')
 * @returns {Object} Date ranges object with moment objects in the specified timezone
 */
export const getTimezoneAwareRanges = (timezone = 'UTC') => {
  const getThisQuarter = timezone === 'UTC'
    ? moment.utc().startOf('quarter')
    : moment.tz(timezone).startOf('quarter');
  const getPreviousQuarter = timezone === 'UTC'
    ? moment.utc().subtract(1, 'Q').startOf('quarter')
    : moment.tz(timezone).subtract(1, 'Q').startOf('quarter');

  const now = () => timezone === 'UTC' ? moment.utc() : moment.tz(timezone);

  return {
    Yesterday: [now().subtract(1, 'days').startOf('day'), now().endOf('day')],
    'Last 7 Days': [now().subtract(7, 'days').startOf('day'), now().endOf('day')],
    'Last Week': [
      now().subtract(1, 'week').startOf('week'),
      now().subtract(1, 'week').endOf('week'),
    ],
    'This Month': [now().startOf('month').startOf('day'), now().endOf('day')],
    'Last Month': [
      now().subtract(1, 'month').startOf('month'),
      now().subtract(1, 'month').endOf('month'),
    ],
    'Last 30 Days': [now().subtract(30, 'days').startOf('day'), now().endOf('day')],
    'This Quarter': [getThisQuarter.clone().startOf('day'), now().endOf('day')],
    'Last Quarter': [getPreviousQuarter.clone().startOf('day'), getThisQuarter.clone().startOf('day')],
    'All Time': [],
  };
};

// Default ranges for backward compatibility (uses UTC)
export const ranges = getTimezoneAwareRanges('UTC');
function filterData(data, startDate, endDate, xAxis = 'date') {
  // filter data to get only between range dates
  // const x = data.filter((obj) => {
  //   return (
  //     moment(obj[xAxis], 'YYYY-MM-DD').isSameOrAfter(startDate) &&
  //     moment(obj[xAxis], 'YYYY-MM-DD').isSameOrBefore(endDate)
  //   );
  // });
  return data;
}

/**
 * This hook is for byDate charts
 * Mainly applicable for objects with xAxis as date
 */
function useByDateFilter(data, xAxis = 'date', id, defaultStartEndDate, noDateChart = false) {
  const [rangeKey, setRangeKey] = useState('Custom');
  const [startDateEndDate, setStartDateEndDate] = useState(ranges['Last 7 Days']);
  const [filteredData, setFilteredData] = useState(data);
  const [customDateRange, setCustomDateRange] = useState([]);
  const [emptyData, setEmptyData] = useState(false);
  const [currentId, setCurrentId] = useState();

  useEffect(() => {
    if (rangeKey && Object.keys(ranges).indexOf(rangeKey) >= 0) {
      setRangeKey(rangeKey);
    }
  }, [data, rangeKey]);

  //Date ranges change for the chart
  useEffect(() => {
    if (customDateRange && customDateRange.length === 2) {
      try {
        const utcStartDate = moment(customDateRange[0], 'YYYY-MM-DD');
        const utcEndDate = moment(customDateRange[1], 'YYYY-MM-DD');

        let filtered = filterData(data, utcStartDate, utcEndDate, xAxis);
        //Add blank data if no data is present for the date
        if (filtered.length === 0) {
          for (var m = utcStartDate; m.diff(utcEndDate, 'days') <= 0; m.add(1, 'days')) {
            let blankData = {};
            blankData[xAxis] = m.format('YYYY-MM-DD');
            filtered.push(blankData);
          }
          //setEmptyData(true);
        } else setEmptyData(false);
        setFilteredData(filtered);
        setStartDateEndDate(customDateRange);
      } catch (e) {
        console.log(e);
      }
    } else {
      setEmptyData(false);
      setFilteredData(data);
    }
  }, [data, customDateRange, xAxis]);

  //Initial Render of the chart
  useEffect(() => {
    if (data && data.length > 0 && (!currentId || currentId !== id)) {
      const sortedByDate = data.sort(
        (a, b) => moment(a[xAxis]).utc(true).unix() - moment(b[xAxis]).utc(true).unix()
      );

      // get startdate and end date
      const startDate = sortedByDate[0][xAxis]; // last 30 days
      const endDate = sortedByDate.slice(-1)[0][xAxis];

      // combined start date & end date
      const startEndDate = [moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD')];

      if (defaultStartEndDate && defaultStartEndDate.length === 2) {
        setStartDateEndDate(defaultStartEndDate);
      } else {
        setStartDateEndDate(startEndDate);
      }

      if (noDateChart) {
        setFilteredData(data);
      } else {
        setFilteredData(filterData(data, startEndDate[0], startEndDate[1], xAxis));
      }
      setCurrentId(id);
    }
  }, [currentId, data, defaultStartEndDate, id, noDateChart, xAxis]);

  return {
    rangeKey,
    setRangeKey,
    filteredData,
    customDateRange,
    setCustomDateRange,
    setStartDateEndDate,
    startDateEndDate,
    emptyData,
  };
}

export { useByDateFilter };
